import {useCallback, useState} from 'react';
import {call, select, takeLatest} from 'redux-saga/effects';
import {channel} from "redux-saga";

import {useStoreCommentList, useStoreCommentThread} from "../store/hooks";
import {actions, useSagaCallback, useSagaEffect} from '../store/sagas';
import {selectCommentsPage, selectCommentVoteState} from "../store/selectors";
import {changeVote} from "../utils/votes";
import {useRecaptcha} from "../utils/recaptcha";
import {apiGet, apiPost, apiPostWithCaptcha} from './client';

function* loadFirstPage(key, url) {
    const {comments, has_more_entries: more} = yield call(apiGet, url);
    yield actions.commentListLoaded(key, comments.map(c => c.id), comments, more);
}

function* loadNextPage(key, url) {
    const page = (yield select(selectCommentsPage(key))) + 1;
    yield actions.commentListLoading(key);
    try {
        const {comments, has_more_entries: more} = yield call(apiGet, `${url}&page=${page}`);
        yield actions.commentListExtended(key, comments.map(c => c.id), comments, page, more);
    } catch {
        yield actions.commentListLoadFailed(key);
    }
}

function* loadCommentList(key, url, actionCh) {
    yield takeLatest(actionCh, function* ({ type }) {
        switch (type) {
            case 'loadNextPage':
                yield call(loadNextPage, key, url);
                break;
            case 'refresh':
                yield call(loadFirstPage, key, url);
                break;
            default:
                throw new Error(type);
        }
    });
    yield takeLatest('userChanged', function* () {
        yield call(loadFirstPage, key, url);
    });
    yield call(loadFirstPage, key, url);
}

export const useProfileComments = (feedbackCh, username, sort, page, community) => {
    const key = `u/${username}:${sort}:${page}:${community}`;
    const [actionCh] = useState(channel);
    useSagaEffect(feedbackCh, useCallback(function* () {
        const url = `/api/v2/comment/profile.json?user=${username}&sort=${sort}&page=${page}&community=${community}`;
        yield call(loadCommentList, key, url, community, actionCh);
    }, [key, username, actionCh]));
    const refresh = useCallback(() => actionCh.put({ type: 'refresh' }), [actionCh]);
    const loadNextPage = useCallback(() => actionCh.put({ type: 'loadNextPage' }), [actionCh]);
    return { ...useStoreCommentList(key), refresh, loadNextPage };
};

export const useCommentThread = (feedbackCh, id, sort, depth) => {
    const key = `c/${id}:${sort}:${depth}`;
    useSagaEffect(feedbackCh, useCallback(function* () {
        let url = `/api/v2/comment/thread?id=${id}`;
        if (typeof sort === 'string') {
            url = `${url}&sort=${sort}`;
        }
        if (typeof depth === 'number') {
            url = `${url}&d=${depth}`;
        }
        const { comments, top_level_comments } = yield call(apiGet, url);
        yield actions.commentListLoaded(key, [comments[0].id], comments);
    }, [key, id, sort, depth]));
    return useStoreCommentThread(key);
};

export const useCommentVote = (feedbackCh) => {
    return useSagaCallback(feedbackCh, useCallback(function* (comment, dir) {
        const voteState = yield select(selectCommentVoteState(comment.id));
        yield actions.commentVoteChanged(comment.id, changeVote(voteState, dir));
        try {
            yield call(apiPost, `/api/v2/action/vote`, {
                id: comment.id,
                type: false /* comment */,
                direction: dir,
            });
        } catch (ex) {
            yield actions.commentVoteChanged(comment.id, voteState);
            throw ex;
        }
    }, []));
};


export const useCreateComment = (feedbackCh) => {
    const recaptcha = useRecaptcha();
    return useSagaCallback(feedbackCh, useCallback(function* (community, parentId, commentParentId, content) {
        const {comments} = yield call(apiPostWithCaptcha, `/api/v2/action/create_comment`, {
            community, parentId, commentParentId, content
        }, recaptcha);
        yield actions.commentCreated(comments[0]);
        return comments[0];
    }, [recaptcha]));
};

export const useCommunityComments = (feedbackCh, community, page) => {
    const key = `c/${community}:${page}`;
    const [actionCh] = useState(channel);
    useSagaEffect(feedbackCh, useCallback(function* () {
        const url = `/api/v2/comment/community.json?community=${community}&page=${page}`;
        yield call(loadCommentList, key, url, actionCh);
    }, [key, community, actionCh]));
    const refresh = useCallback(() => actionCh.put({ type: 'refresh' }), [actionCh]);
    const loadNextPage = useCallback(() => actionCh.put({ type: 'loadNextPage' }), [actionCh]);
    return { ...useStoreCommentList(key), refresh, loadNextPage };
};